import * as React from "react";
import { FilterBar, FilterChip, SmartSearch, type FilterOption } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/* Option sets mirror the unified Studios donor's filter / sort values. */
const ROLE_OPTIONS: FilterOption[] = [
  { value: "all", label: "All roles" },
  { value: "owner", label: "Owner" },
  { value: "editor", label: "Editor" },
  { value: "viewer", label: "Viewer" },
];

const VIS_OPTIONS: FilterOption[] = [
  { value: "all", label: "All visibility" },
  { value: "public", label: "Public" },
  { value: "team", label: "Team" },
  { value: "private", label: "Private" },
];

const STATUS_OPTIONS: FilterOption[] = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All" },
];

const SORT_OPTIONS: FilterOption[] = [
  { value: "recent", label: "Most recent" },
  { value: "name", label: "Name (A-Z)" },
  { value: "activity", label: "Most active" },
];

const TAG_OPTIONS: FilterOption[] = [
  { value: "design", label: "Design" },
  { value: "research", label: "Research" },
  { value: "ops", label: "Operations" },
];

export function FilterBarDemo() {
  // Studios preset: search slot + Role / Visibility / Status chips + a
  // right-aligned Sort chip. Fully controlled, the complete donor contract.
  const [studioSearch, setStudioSearch] = React.useState("");
  const [role, setRole] = React.useState("all");
  const [vis, setVis] = React.useState("all");
  const [status, setStatus] = React.useState("active");
  const [sort, setSort] = React.useState("recent");

  // Agents compact preset: a single-select saved-view chip and a multi-select
  // Filters chip (checkbox mode, count in the chip).
  const [agentSearch, setAgentSearch] = React.useState("");
  const [view, setView] = React.useState("all");
  const [filters, setFilters] = React.useState<string[]>([]);

  // Chip-only, neutral tone, left-packed.
  const [chipStatus, setChipStatus] = React.useState("active");

  return (
    <Section
      id="filter-bar"
      title="FilterBar"
      description="Config-driven listing toolbar: an optional SmartSearch slot, a row of FilterChip controls, a right-aligned sort chip, and a slot for consumer-owned actions. Consolidates the hand-built search + filter + sort toolbars duplicated across the listing surfaces; chips ride the library's Radix DropdownMenu for free focus-trap, type-ahead, and keyboard nav."
    >
      <VariantBlock
        label="Studios"
        caption="search + Role / Visibility / Status chips, Sort pushed right (align=spread)"
      >
        <div className="w-[760px] max-w-full">
          <FilterBar
            searchSlot={
              <SmartSearch
                value={studioSearch}
                onChange={setStudioSearch}
                placeholderPrompts={[
                  "Search your studios...",
                  "Search by name or tag...",
                  "Find a studio by name...",
                ]}
              />
            }
            sort={
              <FilterChip
                label="Sort"
                value={sort}
                defaultValue="recent"
                onValueChange={setSort}
                options={SORT_OPTIONS}
                align="end"
              />
            }
          >
            <FilterChip
              label="Role"
              value={role}
              defaultValue="all"
              onValueChange={setRole}
              options={ROLE_OPTIONS}
            />
            <FilterChip
              label="Visibility"
              value={vis}
              defaultValue="all"
              onValueChange={setVis}
              options={VIS_OPTIONS}
            />
            <FilterChip
              label="Status"
              value={status}
              defaultValue="active"
              onValueChange={setStatus}
              options={STATUS_OPTIONS}
            />
          </FilterBar>
        </div>
      </VariantBlock>

      <VariantBlock
        label="Agents compact"
        caption="size=sm, a saved-view single-select chip plus a multi-select Filters chip"
      >
        <div className="w-[620px] max-w-full">
          <FilterBar
            size="sm"
            searchSlot={
              <SmartSearch
                value={agentSearch}
                onChange={setAgentSearch}
                placeholderPrompts={["Search agents...", "Find an agent by name..."]}
              />
            }
          >
            <FilterChip
              label="Saved view"
              value={view}
              defaultValue="all"
              onValueChange={setView}
              options={[
                { value: "all", label: "All agents" },
                { value: "mine", label: "My agents" },
                { value: "shared", label: "Shared with me" },
              ]}
            />
            <FilterChip
              label="Filters"
              multi
              values={filters}
              onValuesChange={setFilters}
              options={TAG_OPTIONS}
            />
          </FilterBar>
        </div>
      </VariantBlock>

      <VariantBlock
        label="Chip-only, neutral"
        caption="no search slot, left-packed (align=start), neutral tone"
      >
        <div className="w-[520px] max-w-full">
          <FilterBar align="start">
            <FilterChip
              label="Status"
              tone="neutral"
              value={chipStatus}
              defaultValue="active"
              onValueChange={setChipStatus}
              options={STATUS_OPTIONS}
            />
            <FilterChip
              label="Visibility"
              tone="neutral"
              value="all"
              defaultValue="all"
              onValueChange={() => {}}
              options={VIS_OPTIONS}
            />
          </FilterBar>
        </div>
      </VariantBlock>
    </Section>
  );
}
